import { forwardRef, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { Input, InputProps } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';

/**
 * PasswordInput Component
 *
 * A password input field with toggle functionality to show/hide the password text,
 * designed for enhanced usability while maintaining security.
 *
 * Features:
 * - Toggle between password visibility states
 * - Visual eye icon that changes based on visibility state
 * - Forward ref support for form library integration
 * - Inherits all standard input props
 * - Consistent styling with rounded borders
 *
 * @param {InputProps} props - All standard input props are supported and passed through
 * @param {React.Ref<HTMLInputElement>} ref - Ref forwarded to the underlying input element
 *
 * @example
 * // Basic usage
 * <PasswordInput placeholder="Enter your password" />
 *
 * // With form control
 * <PasswordInput
 *   value={password}
 *   onChange={handleChange}
 *   placeholder="Create a password"
 *   required
 * />
 *
 * // With forwarded ref
 * const inputRef = useRef<HTMLInputElement>(null);
 * <PasswordInput ref={inputRef} />
 */

export const PasswordInput = forwardRef<HTMLInputElement, InputProps>(
  ({ className, style, ...props }, ref) => {
  const [open, setOpen] = useState(false);
  const EyeComponet = open ? EyeSlashIcon : EyeIcon;
  return (
    <div className="grid w-full items-center gap-2">
      <div className="relative">
        <Input
          type={open ? 'text' : 'password'}
          {...props}
          ref={ref}
          className={cn('border rounded-lg pr-11', className)}
          style={style}
        />
        <EyeComponet
          className="absolute right-3 top-1/2 size-5 -translate-y-1/2 text-medium-emphasis"
          onClick={() => setOpen(!open)}
        />
      </div>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';
